// src/pages/Admin/CareerDashboard.js
import { useEffect, useMemo, useState } from "react";
import {
  collection, onSnapshot, orderBy, query,
  updateDoc, doc, serverTimestamp, setDoc,
  addDoc, getDocs, deleteDoc
} from "firebase/firestore";
import {
  ref as storageRef, uploadBytes, getDownloadURL, deleteObject
} from "firebase/storage";
import { db, storage } from "../../firebaseConfig";
import AdminGate from "../../components/AdminGate";

/* ======================== Tiny helpers ======================== */
const cx = (...xs) => xs.filter(Boolean).join(" ");
const fmt = (ts) => {
  try {
    if (!ts) return "—";
    if (typeof ts.toDate === "function") return ts.toDate().toLocaleString();
    const d = new Date(ts);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString();
  } catch { return "—"; }
};
const STATUS = [
  "Applied","Screening","Shortlisted","Interview Scheduled","Interviewed",
  "Selected","Offered","Accepted","Joined","Rejected"
];
const ROLES = ["HR", "HR_HEAD", "CEO"]; // simple role switcher on UI

const Badge = ({ s }) => {
  const v = String(s || "Applied").toLowerCase();
  const map = {
    applied:"bg-slate-100 text-slate-700", screening:"bg-blue-100 text-blue-700",
    shortlisted:"bg-cyan-100 text-cyan-700", "interview scheduled":"bg-amber-100 text-amber-700",
    interviewed:"bg-indigo-100 text-indigo-700", selected:"bg-emerald-100 text-emerald-700",
    offered:"bg-teal-100 text-teal-700", accepted:"bg-green-100 text-green-700",
    joined:"bg-lime-100 text-lime-700", rejected:"bg-rose-100 text-rose-700",
  };
  return <span className={cx("px-2.5 py-1 rounded-full text-xs font-semibold", map[v] || map.applied)}>{s}</span>;
};
function Info({label,value}){ return (<div><div className="text-xs text-slate-500 mb-1">{label}</div><div className="text-sm text-slate-800">{value??"—"}</div></div>); }
function F({label,children}){ return (<div><div className="text-xs text-slate-500 mb-1">{label}</div>{children}</div>); }

/* ==================== Letterhead helpers ===================== */
async function fetchToDataURL(url) {
  try {
    const res = await fetch(url, { cache: "no-cache" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  } catch { return null; }
}
function loadImageElement(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}
async function renderLetterhead(pdf) {
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const candidates = ["/public/letterheads/saam-letterhead.png", "/letterheads/saam-letterhead.png"];
  for (const path of candidates) {
    try {
      const dataURL = await fetchToDataURL(path);
      if (dataURL) { pdf.addImage(dataURL, /\.png/i.test(path)?"PNG":"JPEG", 0, 0, W, H); return true; }
      const img = await loadImageElement(path);
      pdf.addImage(img, 0, 0, W, H); return true;
    } catch { /* try next */ }
  }
  return false;
}

/* =============== Shared PDF primitives (jsPDF) =============== */
function paragraph(pdf, x, y, width, lines) {
  lines.forEach(raw => {
    pdf.splitTextToSize(raw, width).forEach(ln => { pdf.text(ln, x, y); y += 16; });
    if (!raw) y += 2;
  });
  return y;
}
function kv(pdf, x, y, rows, keyW=110, valW=360) {
  pdf.setFont("Helvetica","bold"); pdf.setFontSize(11);
  rows.forEach(([k,v])=>{
    pdf.text(`${k}:`, x, y);
    pdf.setFont("Helvetica","normal");
    pdf.text(String(v ?? "—"), x+keyW, y, { maxWidth: valW });
    pdf.setFont("Helvetica","bold");
    y += 16;
  });
  return y;
}
function ruler(pdf, x1, y, x2, color="#e5e7eb"){ pdf.setDrawColor(color); pdf.line(x1,y,x2,y); }

/* ===================== Letter generators ===================== */
/**
 * generatePDF(type, app, opts)
 * type: 'interview' | 'offer' | 'joining' | 'reject'
 * opts: per type fields
 */
async function generatePDF(type, app, opts = {}) {
  const { default: jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const W = pdf.internal.pageSize.getWidth();
  const LM = 64, RM = 64;
  let y = 172;
  const FG="#0f172a", MUTED="#475569", RULE="#e5e7eb";

  await renderLetterhead(pdf);

  const TITLES = {
    interview: "INTERVIEW INVITATION",
    offer: "OFFER OF EMPLOYMENT",
    joining: "JOINING LETTER",
    reject: "REGRET LETTER",
  };

  // Title
  pdf.setTextColor(FG); pdf.setFont("Helvetica","bold"); pdf.setFontSize(22);
  pdf.text(TITLES[type] || "LETTER", LM, y); ruler(pdf, LM, y+12, W-RM, RULE); y += 32;

  // Capsule header
  const capW = W-LM-RM, capH = 90, capX=LM;
  pdf.setFillColor(248,250,252); pdf.roundedRect(capX,y,capW,capH,12,12,"F");
  pdf.setDrawColor("#dbe0e6"); pdf.roundedRect(capX,y,capW,capH,12,12,"S");
  pdf.setFillColor(37,99,235); pdf.roundedRect(capX,y,6,capH,12,0,"F");

  pdf.setTextColor(FG); pdf.setFont("Helvetica","bold"); pdf.setFontSize(14);
  pdf.text((app.name||"Candidate").toUpperCase(), capX+18, y+26);
  pdf.setFont("Helvetica","normal"); pdf.setFontSize(11); pdf.setTextColor(MUTED);
  pdf.text(`${app.email || "—"}   •   +91-${app.mobile || "—"}`, capX+18, y+46);
  const rhs = capX+capW-18;
  pdf.setTextColor(FG); pdf.setFont("Helvetica","bold"); pdf.setFontSize(12);
  pdf.text(app.jobTitle || app.postTitle || app.post || "—", rhs, y+26, {align:"right"});
  pdf.setFont("Helvetica","normal"); pdf.setTextColor(MUTED);
  pdf.text(app.deptName || app.department || "—", rhs, y+44, {align:"right"});
  pdf.text((app.city && app.state) ? `${app.city}, ${app.state}` : (app.city || app.state || " "), rhs, y+62, {align:"right"});
  y += capH + 22;

  // Body per type
  pdf.setTextColor(FG); pdf.setFont("Helvetica","normal"); pdf.setFontSize(12);
  if (type === "interview") {
    y = paragraph(pdf, LM, y, capW, [
      `Dear ${(app.name || "Candidate").toUpperCase()},`,
      "",
      `We are pleased to invite you for an interview for the position of “${app.jobTitle || "—"}”.`,
      "Kindly find the schedule and venue details below.",
      "",
    ]);
    y = kv(pdf, LM, y, [
      ["Date", opts.date || "—"],
      ["Time", opts.time || "—"],
      ["Mode/Venue", opts.venue || "—"],
      ["Interviewer", opts.interviewer || "HR Panel"],
      ["Documents", "Resume, Aadhaar/PAN, Certificates"],
    ]);
    y += 10;
    y = paragraph(pdf, LM, y, capW, [
      "Please reach 10 minutes before time. In case of any query, reply to this email.",
      "", "Regards,", "HR Department",
    ]);
  }

  if (type === "offer") {
    const money = (v)=> v?`₹ ${String(v).replace(/[^\d.]/g,"").replace(/\B(?=(\d{3})+(?!\d))/g,",")}`:"—";
    y = paragraph(pdf, LM, y, capW, [
      `Dear ${(app.name || "Candidate").toUpperCase()},`,
      "",
      `We are delighted to offer you the role of “${opts.designation || app.jobTitle || "—"}”.`,
      "Your appointment is subject to successful verification and company policies.",
      "",
    ]);
    const leftX = LM, rightX = LM + capW/2 + 8; let ly=y, ry=y;
    ly = kv(pdf,leftX,ly,[
      ["Designation", opts.designation || app.jobTitle || "—"],
      ["Joining", opts.joiningDate || "—"],
      ["Work Location", opts.workLocation || "—"],
    ]);
    ry = kv(pdf,rightX,ry,[
      ["CTC (Annual)", money(opts.ctc)],
      ["Level/Grade", opts.level || "—"],
      ["Reporting To", opts.reportingTo || "HR"],
    ]);
    y = Math.max(ly,ry) + 12; ruler(pdf, LM, y, W-RM, RULE); y += 10;
    y = paragraph(pdf, LM, y, capW, [
      "Please sign and return a copy of this letter as a token of acceptance within 3 days of receipt.",
      "", "Regards,", opts.signName || "HR Manager", opts.signTitle || "HR Department",
    ]);
  }

  if (type === "joining") {
    y = paragraph(pdf, LM, y, capW, [
      `Dear ${(app.name || "Candidate").toUpperCase()},`,
      "",
      `We are happy to welcome you to “${opts.employerName || "Shine Agri Allied & Marketing"}” as “${opts.designation || app.jobTitle || "—"}”.`,
      `Your date of joining is ${opts.joiningDate || "—"} at ${opts.reportingTime || "10:00 AM"}. Please report to HR Desk.`,
      "",
    ]);
    y = kv(pdf, LM, y, [
      ["Office Address", opts.officeAddress || "Head Office"],
      ["Documents", "Original ID, 2 Photos, Certificates, Relieving/Salary proofs"],
      ["HR Contact", opts.hrContact || "+91-XXXXXXXXXX"],
    ]);
    y += 10;
    y = paragraph(pdf, LM, y, capW, ["We look forward to a long and productive association.", "", "Regards,", "HR Department"]);
  }

  if (type === "reject") {
    y = paragraph(pdf, LM, y, capW, [
      `Dear ${(app.name || "Candidate").toUpperCase()},`,
      "",
      "Thank you for your interest and time. After careful consideration, we will not be moving forward with your application at this time.",
      "We appreciate your effort and wish you success in your future endeavors.",
      "", "Regards,", "HR Department",
    ]);
  }

  return pdf.output("blob");
}

/* ======================= Dashboard ======================== */
export default function CareerDashboard() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters
  const [q, setQ] = useState("");
  const [stat, setStat] = useState("");
  const [sort, setSort] = useState("appliedAt:desc");
  const [page, setPage] = useState(1);
  const [ps, setPS] = useState(10);

  // modals
  const [view, setView] = useState(null);            // view drawer & letter modal base
  const [letterType, setLetterType] = useState("");  // 'interview' | 'offer' | 'joining' | 'reject'
  const [inputs, setInputs] = useState({
    // interview
    date:"", time:"", venue:"", interviewer:"",
    // offer
    ctc:"", joiningDate:"", workLocation:"", designation:"", level:"", reportingTo:"", signName:"", signTitle:"",
    // joining
    employerName:"Shine Agri Allied & Marketing", reportingTime:"10:00 AM", officeAddress:"", hrContact:"",
  });

  // approvals
  const [role, setRole] = useState("HR"); // HR | HR_HEAD | CEO
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveNote, setApproveNote] = useState("");

  // hr note for audits
  const [note, setNote] = useState("");

  // subscribe
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "career_applications"), orderBy("appliedAt", "desc")),
      (snap) => { setApps(snap.docs.map(d => ({ id:d.id, ...d.data() }))); setLoading(false); },
      (e) => { console.error(e); setLoading(false); }
    );
    return () => unsub();
  }, []);

  // filter/sort/paginate
  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    let r = apps.filter(a => {
      if (stat && String(a.status||"Applied").toLowerCase() !== stat.toLowerCase()) return false;
      if (!t) return true;
      const hay = `${a.name||""} ${a.email||""} ${a.mobile||""} ${a.jobTitle||a.postTitle||a.post||""}`.toLowerCase();
      return hay.includes(t);
    });
    const [k,dir="asc"] = sort.split(":"); const m = dir==="asc"?1:-1;
    r.sort((a,b)=>{
      if (k==="appliedAt") {
        const aa = a.appliedAt?.toDate? a.appliedAt.toDate().getTime() : new Date(a.appliedAt||0).getTime();
        const bb = b.appliedAt?.toDate? b.appliedAt.toDate().getTime() : new Date(b.appliedAt||0).getTime();
        return (aa-bb)*m;
      }
      if (k==="name")  return String(a.name||"").localeCompare(String(b.name||""))*m;
      if (k==="status")return String(a.status||"").localeCompare(String(b.status||""))*m;
      if (k==="post")  return String(a.jobTitle||a.postTitle||a.post||"").localeCompare(String(b.jobTitle||b.postTitle||b.post||""))*m;
      return 0;
    });
    return r;
  }, [apps,q,stat,sort]);

  const total = Math.max(1, Math.ceil(list.length/ps));
  const pageData = list.slice((page-1)*ps, page*ps);

  /* ======================= Mutations ======================= */
  async function audit(appId, action, extra = {}) {
    await setDoc(doc(collection(db, "career_applications", appId, "audits")), {
      action, at: serverTimestamp(), notes: note || "", ...extra
    });
    setNote("");
  }
  async function setStatus(id, status, extra = {}) {
    try {
      await updateDoc(doc(db, "career_applications", id), { status, ...extra, updatedAt: serverTimestamp() });
      await audit(id, `status:${status}`, extra);
    } catch (e) {
      console.error("[setStatus]", e); alert(`Update failed: ${e?.message || e}`);
    }
  }
  async function uploadAndLink(app, blob, keyName) {
    const safe = (app.appId || app.id || "app").toString().replace(/[^\w\-]+/g,"_");
    const path = `career_letters/${safe}/${keyName}.pdf`;
    const sRef = storageRef(storage, path);
    await uploadBytes(sRef, blob, { contentType:"application/pdf" });
    const url = await getDownloadURL(sRef);
    const ref = doc(db,"career_applications",app.id);
    const patch={}; patch[`${keyName}Url`]=url; patch[`${keyName}Date`]=serverTimestamp();
    await updateDoc(ref, patch);
    await audit(app.id, `letter:${keyName}`, { url });
    return url;
  }
  function dl(blob, fname) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = fname; a.click();
    URL.revokeObjectURL(url);
  }

  // letters
  async function handleGenerate(app, type) {
    setView(app);
    setLetterType(type);
  }
  async function confirmGenerate() {
    if (!view || !letterType) return;
    try {
      const app = view;
      const blob = await generatePDF(letterType, app, inputs);
      const keyMap = { interview:"interviewLetter", offer:"offerLetter", joining:"joiningLetter", reject:"rejectLetter" };
      const filename = `${(app.name||"candidate").replace(/[^a-z0-9]/gi,"_").toLowerCase()}_${letterType}.pdf`;

      dl(blob, filename);
      await uploadAndLink(app, blob, keyMap[letterType]);

      const statusMap = {
        interview: "Interview Scheduled",
        offer: role==="HR" ? "Selected" : "Offered", // HR can mark 'Selected' and forward to approval
        joining: "Joined",
        reject: "Rejected",
      };
      await setStatus(app.id, statusMap[letterType] || app.status || "Applied");

      // create approval tasks when needed
      if (letterType === "offer") {
        // HR makes an approval request for HR_HEAD first
        await addApproval(app.id, "Offer", "HR_HEAD", "Pending", { payload: inputs });
      }
      if (letterType === "joining") {
        // HR_HEAD approval for joining, then CEO final
        await addApproval(app.id, "Joining", "HR_HEAD", "Pending", { payload: inputs });
      }

      setLetterType(""); setView(null);
    } catch (e) {
      console.error("[confirmGenerate]", e);
      alert(`Letter failed: ${e?.message || e}`);
    }
  }

  // approvals
  async function addApproval(appId, type, roleNeeded, status="Pending", extra = {}) {
    await addDoc(collection(db, "career_applications", appId, "approvals"), {
      type, requiredRole: roleNeeded, status, createdAt: serverTimestamp(), ...extra
    });
    await audit(appId, `approval:request`, { type, role: roleNeeded });
  }
  async function actApproval(app, approval, action) {
    // action: Approve / Reject; role: HR_HEAD or CEO
    const newStatus = action === "Approve" ? "Approved" : "Rejected";
    await updateDoc(doc(db, "career_applications", app.id, "approvals", approval.id), {
      status: newStatus, actionBy: role, actionAt: serverTimestamp(), remarks: approveNote || ""
    });
    await audit(app.id, `approval:${action}`, { type: approval.type, role, remarks: approveNote || "" });

    // chain approvals: if HR_HEAD approves Offer → create CEO approval; if CEO approves Offer → status Offered
    if (approval.type === "Offer" && action === "Approve") {
      if (role === "HR_HEAD") {
        await addApproval(app.id, "Offer", "CEO", "Pending", { payload: approval.payload || {} });
      } else if (role === "CEO") {
        await setStatus(app.id, "Offered"); // final
      }
    }
    if (approval.type === "Joining" && action === "Approve") {
      if (role === "HR_HEAD") {
        await addApproval(app.id, "Joining", "CEO", "Pending", { payload: approval.payload || {} });
      } else if (role === "CEO") {
        await setStatus(app.id, "Joined");
      }
    }
    setApproveNote("");
    setApproveOpen(false);
  }

  // deletion (doc + audits + approvals + PDFs)
  async function handleDelete(app) {
    const ok = window.confirm(`Delete application for ${app.name}? This can also delete audits/approvals and uploaded letters.`);
    if (!ok) return;

    try {
      // delete uploaded letter PDFs if URLs exist
      const keys = ["interviewLetterUrl","offerLetterUrl","joiningLetterUrl","rejectLetterUrl"];
      for (const k of keys) {
        if (app[k]) {
          try { await deleteObject(storageRef(storage, app[k])); } catch (er) { /* ignore */ }
        }
      }

      // delete audits
      try {
        const audits = await getDocs(collection(db, "career_applications", app.id, "audits"));
        await Promise.all(audits.docs.map(d=>deleteDoc(doc(db, "career_applications", app.id, "audits", d.id))));
      } catch (er) { /* ignore */ }

      // delete approvals
      try {
        const approvals = await getDocs(collection(db, "career_applications", app.id, "approvals"));
        await Promise.all(approvals.docs.map(d=>deleteDoc(doc(db, "career_applications", app.id, "approvals", d.id))));
      } catch (er) { /* ignore */ }

      // finally delete application
      await deleteDoc(doc(db, "career_applications", app.id));
      alert("Application deleted.");
    } catch (e) {
      console.error("[delete]", e);
      alert(`Delete failed: ${e?.message || e}`);
    }
  }

  /* =========================== UI =========================== */
  return (
    <AdminGate>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Logout Bar */}
<div className="flex justify-end mb-4">
  <button
    onClick={() => {
      import("../../services/adminAuth").then(({ adminLogout }) => {
        adminLogout();
        window.location.href = "/admin-login";
      });
    }}
    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
  >
    Logout
  </button>
</div>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-700 via-purple-600 to-blue-600">
                Career Dashboard
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                Full HR flow with approvals — generate letters on letterhead, upload, audit, and CEO approval.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-600">Role</span>
                <select value={role} onChange={e=>setRole(e.target.value)}
                        className="border border-slate-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  {ROLES.map(r=><option key={r}>{r}</option>)}
                </select>
              </div>
              <input value={note} onChange={e=>setNote(e.target.value)}
                     placeholder="HR note (saved in audit)…"
                     className="w-72 border border-slate-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent"/>
              <button onClick={()=>window.location.reload()}
                      className="px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
                Refresh
              </button>
            </div>
          </div>

          {/* Toolbar */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mt-5 mb-5">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
              <div className="flex items-center gap-3 flex-1">
                <input value={q} onChange={e=>{setQ(e.target.value); setPage(1);}}
                       placeholder="Search name / email / mobile / post…"
                       className="flex-1 min-w-[220px] border border-slate-300 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent"/>
                <select value={stat} onChange={e=>{setStat(e.target.value); setPage(1);}}
                        className="border border-slate-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent">
                  <option value="">All Status</option>
                  {STATUS.map(s=><option key={s} value={s.toLowerCase()}>{s}</option>)}
                </select>
                <select value={sort} onChange={e=>setSort(e.target.value)}
                        className="border border-slate-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent">
                  <option value="appliedAt:desc">Applied ↓</option>
                  <option value="appliedAt:asc">Applied ↑</option>
                  <option value="name:asc">Name A→Z</option>
                  <option value="name:desc">Name Z→A</option>
                  <option value="status:asc">Status A→Z</option>
                  <option value="status:desc">Status Z→A</option>
                  <option value="post:asc">Post A→Z</option>
                  <option value="post:desc">Post Z→A</option>
                </select>
              </div>
              <div className="text-sm text-slate-600">Showing <b>{list.length}</b> application(s)</div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="w-full overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="p-3 text-left">Candidate</th>
                    <th className="p-3 text-left">Post</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">Applied</th>
                    <th className="p-3 text-left w-[740px]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={5} className="p-8 text-center text-slate-500">Loading…</td></tr>
                  ) : pageData.length === 0 ? (
                    <tr><td colSpan={5} className="p-8 text-center text-slate-500">No applications found.</td></tr>
                  ) : (
                    pageData.map(a=>(
                      <tr key={a.id} className="border-t hover:bg-slate-50">
                        <td className="p-3">
                          <div className="font-semibold text-slate-900">{a.name||"—"}</div>
                          <div className="text-xs text-slate-600">{a.email||"—"} • {a.mobile||"—"}</div>
                        </td>
                        <td className="p-3">{a.jobTitle||a.postTitle||a.post||"—"}</td>
                        <td className="p-3"><Badge s={a.status||"Applied"}/></td>
                        <td className="p-3 text-xs text-slate-600">{fmt(a.appliedAt)}</td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-2">
                            {role==="HR" && (
                              <>
                                <button onClick={()=>setStatus(a.id,"Screening")}  className="btn bg-slate-200 text-slate-800">Screening</button>
                                <button onClick={()=>setStatus(a.id,"Shortlisted")} className="btn bg-cyan-200 text-cyan-800">Shortlist</button>
                                <button onClick={()=>handleGenerate(a,"interview")} className="btn bg-amber-600 text-white">Interview Letter</button>
                                <button onClick={()=>handleGenerate(a,"offer")}     className="btn bg-emerald-600 text-white">Offer Letter</button>
                                <button onClick={()=>handleGenerate(a,"joining")}   className="btn bg-indigo-600 text-white">Joining Letter</button>
                                <button onClick={()=>handleGenerate(a,"reject")}    className="btn bg-rose-600 text-white">Reject Letter</button>
                              </>
                            )}
                            {role!=="HR" && (
                              <button onClick={()=>setView(a)} className="btn bg-slate-800 text-white">View</button>
                            )}
                            {/* Approvals button visible for HR_HEAD and CEO */}
                            {role!=="HR" && (
                              <button className="btn bg-teal-700 text-white" onClick={()=>{setView(a); setApproveOpen(true);}}>
                                Approvals
                              </button>
                            )}
                            {/* Delete (always visible) */}
                            <button className="btn bg-slate-100 text-slate-800 border border-slate-300" onClick={()=>handleDelete(a)}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between p-4 border-t bg-slate-50">
              <div className="text-xs text-slate-600">
                Showing {(page-1)*ps+1}–{Math.min(page*ps,list.length)} of {list.length}
              </div>
              <div className="flex items-center gap-2">
                <button className="pg" onClick={()=>setPage(1)} disabled={page===1}>First</button>
                <button className="pg" onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}>Prev</button>
                <div className="px-3 py-1 text-sm text-slate-700">{page} / {total}</div>
                <button className="pg" onClick={()=>setPage(p=>Math.min(total,p+1))} disabled={page===total}>Next</button>
                <button className="pg" onClick={()=>setPage(total)} disabled={page===total}>Last</button>
                <select value={ps} onChange={e=>{setPS(Number(e.target.value)); setPage(1);}}
                        className="ml-2 border border-slate-300 rounded-lg px-2 py-1 text-sm">
                  <option value={5}>5 / page</option>
                  <option value={10}>10 / page</option>
                  <option value={20}>20 / page</option>
                </select>
              </div>
              <style>{`.btn{padding:.35rem .65rem;border-radius:.55rem;font-size:.72rem} .pg{padding:.25rem .6rem;border:1px solid #cbd5e1;border-radius:.5rem;font-size:.85rem}`}</style>
            </div>
          </div>

          {/* VIEW drawer (compact) */}
          {view && !letterType && !approveOpen && (
            <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50 p-4">
              <div className="bg-white w-full max-w-3xl rounded-2xl p-6 shadow-2xl relative">
                <button className="absolute top-4 right-4 text-2xl text-slate-500 hover:text-slate-700" onClick={()=>setView(null)}>×</button>
                <h3 className="text-lg font-bold text-slate-900 mb-4">{view.name} — {view.jobTitle||view.postTitle||view.post||"—"}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Info label="Email" value={view.email}/>
                  <Info label="Mobile" value={view.mobile}/>
                  <Info label="Status" value={<Badge s={view.status||"Applied"}/>}/>
                  <Info label="Applied" value={fmt(view.appliedAt)}/>
                </div>
                {/* Timeline / audits */}
                <Audits appId={view.id} />
                <div className="mt-5 text-right">
                  <button className="px-4 py-2 rounded-lg bg-slate-200 text-slate-800 hover:bg-slate-300" onClick={()=>setView(null)}>Close</button>
                </div>
              </div>
            </div>
          )}

          {/* LETTER MODAL */}
          {letterType && view && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <div className="bg-white rounded-2xl w-full max-w-3xl p-6 relative shadow-2xl">
                <button className="absolute top-4 right-4 text-2xl text-slate-500 hover:text-slate-700" onClick={()=>{setLetterType(""); setView(null);}}>×</button>
                <h3 className="text-lg font-bold text-slate-900 mb-4">
                  {letterType === "interview" && "Create Interview Letter"}
                  {letterType === "offer"     && "Create Offer Letter"}
                  {letterType === "joining"   && "Create Joining Letter"}
                  {letterType === "reject"    && "Create Regret Letter"}
                  {" — "}{view.name}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {letterType === "interview" && (
                    <>
                      <F label="Date"><input className="i" type="date" value={inputs.date} onChange={e=>setInputs(i=>({...i,date:e.target.value}))}/></F>
                      <F label="Time"><input className="i" value={inputs.time} onChange={e=>setInputs(i=>({...i,time:e.target.value}))}/></F>
                      <F label="Mode/Venue"><input className="i" value={inputs.venue} onChange={e=>setInputs(i=>({...i,venue:e.target.value}))}/></F>
                      <F label="Interviewer"><input className="i" value={inputs.interviewer} onChange={e=>setInputs(i=>({...i,interviewer:e.target.value}))}/></F>
                    </>
                  )}
                  {letterType === "offer" && (
                    <>
                      <F label="CTC (Annual ₹)"><input className="i" value={inputs.ctc} onChange={e=>setInputs(i=>({...i,ctc:e.target.value}))}/></F>
                      <F label="Joining Date"><input className="i" type="date" value={inputs.joiningDate} onChange={e=>setInputs(i=>({...i,joiningDate:e.target.value}))}/></F>
                      <F label="Work Location"><input className="i" value={inputs.workLocation} onChange={e=>setInputs(i=>({...i,workLocation:e.target.value}))}/></F>
                      <F label="Designation"><input className="i" value={inputs.designation} onChange={e=>setInputs(i=>({...i,designation:e.target.value}))}/></F>
                      <F label="Level/Grade"><input className="i" value={inputs.level} onChange={e=>setInputs(i=>({...i,level:e.target.value}))}/></F>
                      <F label="Reporting To"><input className="i" value={inputs.reportingTo} onChange={e=>setInputs(i=>({...i,reportingTo:e.target.value}))}/></F>
                      <F label="Signatory Name"><input className="i" value={inputs.signName} onChange={e=>setInputs(i=>({...i,signName:e.target.value}))}/></F>
                      <F label="Signatory Title"><input className="i" value={inputs.signTitle} onChange={e=>setInputs(i=>({...i,signTitle:e.target.value}))}/></F>
                    </>
                  )}
                  {letterType === "joining" && (
                    <>
                      <F label="Employer"><input className="i" value={inputs.employerName} onChange={e=>setInputs(i=>({...i,employerName:e.target.value}))}/></F>
                      <F label="Designation"><input className="i" value={inputs.designation} onChange={e=>setInputs(i=>({...i,designation:e.target.value}))}/></F>
                      <F label="Joining Date"><input className="i" type="date" value={inputs.joiningDate} onChange={e=>setInputs(i=>({...i,joiningDate:e.target.value}))}/></F>
                      <F label="Reporting Time"><input className="i" value={inputs.reportingTime} onChange={e=>setInputs(i=>({...i,reportingTime:e.target.value}))}/></F>
                      <F label="Office Address"><input className="i" value={inputs.officeAddress} onChange={e=>setInputs(i=>({...i,officeAddress:e.target.value}))}/></F>
                      <F label="HR Contact"><input className="i" value={inputs.hrContact} onChange={e=>setInputs(i=>({...i,hrContact:e.target.value}))}/></F>
                    </>
                  )}
                  {letterType === "reject" && (
                    <div className="md:col-span-2 text-slate-600 text-sm">
                      Generates a professional regret letter on your letterhead. No inputs required.
                    </div>
                  )}

                  <div className="md:col-span-2">
                    <div className="text-xs text-slate-500 mb-1">HR Note (will be saved in audits)</div>
                    <input className="i" value={note} onChange={e=>setNote(e.target.value)} placeholder="Optional remarks…"/>
                  </div>
                </div>

                <div className="mt-6 text-right">
                  <button className="px-4 py-2 rounded-lg bg-slate-200 text-slate-800 hover:bg-slate-300 mr-2"
                          onClick={()=>{setLetterType(""); setView(null);}}>
                    Cancel
                  </button>
                  <button className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
                          onClick={confirmGenerate}>
                    Generate, Download & Upload
                  </button>
                </div>
              </div>
              <style>{`.i{width:100%;border:1px solid #cbd5e1;border-radius:.5rem;padding:.5rem .75rem;outline:none}.i:focus{box-shadow:0 0 0 2px #10b981;border-color:transparent}`}</style>
            </div>
          )}

          {/* APPROVALS MODAL (HR_HEAD / CEO) */}
          {approveOpen && view && role!=="HR" && (
            <ApprovalsModal
              app={view}
              role={role}
              onClose={()=>{ setApproveOpen(false); setView(null); }}
              onAct={actApproval}
              note={approveNote}
              setNote={setApproveNote}
            />
          )}
        </div>
      </div>
    </AdminGate>
  );
}

/* =================== Audits (timeline) =================== */
function Audits({ appId }) {
  const [items, setItems] = useState([]);
  useEffect(()=>{
    const unsub = onSnapshot(
      query(collection(db, "career_applications", appId, "audits"), orderBy("at","desc")),
      (snap)=> setItems(snap.docs.map(d=>({ id:d.id, ...d.data() })))
    );
    return ()=>unsub();
  },[appId]);
  return (
    <div className="mt-6">
      <div className="text-sm font-semibold text-slate-900 mb-2">Timeline</div>
      {items.length===0 ? (
        <div className="text-xs text-slate-500">No activity yet.</div>
      ) : (
        <div className="max-h-72 overflow-auto pr-2">
          {items.map(it=>(
            <div key={it.id} className="flex items-start gap-3 py-2 border-b">
              <div className="w-2 h-2 mt-2 rounded-full bg-slate-400" />
              <div className="flex-1">
                <div className="text-sm text-slate-800">{it.action}</div>
                {it.notes && <div className="text-xs text-slate-600">{it.notes}</div>}
                <div className="text-[11px] text-slate-500">{fmt(it.at)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============= Approvals modal (HR_HEAD/CEO) ============= */
function ApprovalsModal({ app, role, onClose, onAct, note, setNote }) {
  const [items, setItems] = useState([]);
  useEffect(()=>{
    const unsub = onSnapshot(
      query(collection(db, "career_applications", app.id, "approvals"), orderBy("createdAt","desc")),
      (snap)=> setItems(snap.docs.map(d=>({ id:d.id, ...d.data() })))
    );
    return ()=>unsub();
  },[app.id]);

  const pending = items.filter(a=>a.requiredRole===role && a.status==="Pending");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl p-6 relative shadow-2xl">
        <button className="absolute top-4 right-4 text-2xl text-slate-500 hover:text-slate-700" onClick={onClose}>×</button>
        <h3 className="text-lg font-bold text-slate-900 mb-4">
          Approvals — {role} — {app.name} ({app.jobTitle||app.postTitle||app.post||"—"})
        </h3>

        {items.length===0 ? (
          <div className="text-sm text-slate-600">No approvals created yet.</div>
        ) : (
          <div className="space-y-3 max-h-80 overflow-auto pr-1">
            {items.map(a=>(
              <div key={a.id} className="border rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-slate-900">{a.type} Approval</div>
                  <div className={cx("text-xs px-2 py-1 rounded-full",
                    a.status==="Pending"?"bg-amber-100 text-amber-800":
                    a.status==="Approved"?"bg-emerald-100 text-emerald-800":"bg-rose-100 text-rose-700")}>
                    {a.status}
                  </div>
                </div>
                <div className="text-xs text-slate-600 mt-1">Required Role: {a.requiredRole}</div>
                {a.remarks && <div className="text-xs text-slate-600">Remarks: {a.remarks}</div>}
                {a.actionBy && <div className="text-xs text-slate-500">By: {a.actionBy}</div>}

                {/* Action buttons for this role if pending */}
                {a.status==="Pending" && a.requiredRole===role && (
                  <div className="mt-3 flex items-center gap-2">
                    <input className="i flex-1" placeholder="Remarks (optional)" value={note} onChange={e=>setNote(e.target.value)}/>
                    <button className="px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
                            onClick={()=>onAct(app, a, "Approve")}>Approve</button>
                    <button className="px-3 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700"
                            onClick={()=>onAct(app, a, "Reject")}>Reject</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 text-right">
          <button className="px-4 py-2 rounded-lg bg-slate-200 text-slate-800 hover:bg-slate-300" onClick={onClose}>Close</button>
        </div>
      </div>
      <style>{`.i{width:100%;border:1px solid #cbd5e1;border-radius:.5rem;padding:.5rem .75rem;outline:none}.i:focus{box-shadow:0 0 0 2px #10b981;border-color:transparent}`}</style>
    </div>
  );
}
